import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { FaEdit, FaTrash } from 'react-icons/fa';
import config from "../../../config/config";
import { getAuthToken } from "../../../utils/token";
import axios from "axios";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader from '../../loader/Loader';

const customStyles = {
  headCells: {
    style: {
      backgroundColor: '#0d6efd',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '14px',
    },
  },
};

export default function Product() {
  const [dataList, setDataList] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [backendMsg,setBackendMsg]=useState("")

  const initialState = {
    product_code: "",
    name: "",
    category: "",
    unit: "",
    description: "",
    cost_price: "",
    sale_price: "",
    stock: "",
    location: "",
    is_active: true
  };

  const [form, setForm] = useState(initialState);

  useEffect(() => {
    fetchList();
  }, []);

  const fetchList = async () => {
    const token = getAuthToken();
    if (!token) return;
    setListLoading(true);
    try {
      const res = await axios.get(`${config.API}/product/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDataList(res.data.products || []);
    } catch (err) {
      console.error("Error fetching product list:", err);
      toast.error("Failed to load data");
    } finally {
      setListLoading(false);
    }
  };

  const handleInput = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const openAddModal = () => {
    setForm(initialState);
    setIsEditing(false);
    setEditingId(null);
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setForm({ ...item });
    setIsEditing(true);
    setEditingId(item.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;

    const token = getAuthToken();
    if (!token) {
      toast.error("Authentication missing");
      return;
    }
    try {
      await axios.delete(`${config.API}/product/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Deleted successfully");
      fetchList();
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Delete failed");
    }
  };

  const handleSubmit = async () => {
    const token = getAuthToken();
    if (!token) {
      toast.error("Authentication missing");
      return;
    }

    const payload = {
      product_code: form.product_code,
      name: form.name,
      category: form.category,
      unit: form.unit,
      description: form.description,
      cost_price: Number(form.cost_price),
      sale_price: Number(form.sale_price),
      stock: Number(form.stock),
      location: form.location,
      is_active: Boolean(form.is_active)
    };

    setFormLoading(true);
    setBackendMsg({}); 

    try {
      let response;

      // UPDATE
      if (isEditing && editingId) {
        response = await axios.put(
          `${config.API}/product/${editingId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      // CREATE
      else {
        response = await axios.post(
          `${config.API}/product/create`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      if (response?.data?.message) {
        toast.success(response.data.message);
      } else {
        toast.success(isEditing ? "Updated successfully" : "Created successfully");
      }

      setShowModal(false);
      fetchList();
    } catch (err) {
      if (err.response?.status === 422) {
        const errors = err.response.data.errors;
        setBackendMsg(errors);
        return;
      }

      const serverMsg = err.response?.data?.message || "Submit failed";
      toast.error(serverMsg);
    } finally {
      setFormLoading(false);
    }
  };

  const filtered = dataList.filter(item =>
    Object.values(item).join(' ').toLowerCase().includes(filterText.toLowerCase())
  );

  const columns = [
    { name: 'ID', selector: row => row.id, sortable: true },
    { name: 'Product Code', selector: row => row.product_code, sortable: true },
    { name: 'Name', selector: row => row.name, sortable: true },
    { name: 'Category', selector: row => row.category, sortable: true },
    { name: 'Unit', selector: row => row.unit, sortable: true },
    { name: 'Cost Price', selector: row => row.cost_price, sortable: true },
    { name: 'Sale Price', selector: row => row.sale_price, sortable: true },
    { name: 'Stock', selector: row => row.stock, sortable: true },
    { name: 'Location', selector: row => row.location, sortable: true },
    { name: 'Active', selector: row => row.is_active ? 'Yes' : 'No', sortable: true },
    {
      name: 'Actions',
      cell: row => (
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-primary" onClick={() => openEditModal(row)}><FaEdit /></button>
          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(row.id)}><FaTrash /></button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true
    }
  ];

  return (
    <div className="container mt-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <strong className="mb-0 fs-4">Product Master</strong>
        <div>
          <input
            type="text"
            className="form-control d-inline-block me-2"
            placeholder="Search..."
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
            style={{ width: '200px' }}
          />
          <button className="btn btn-primary" onClick={openAddModal}>+ Add Product</button>
        </div>
      </div>

      {listLoading ? (
        <div className="text-center"><Loader /></div>
      ) : (
        <DataTable
          columns={columns}
          data={filtered}
          pagination
          highlightOnHover
          customStyles={customStyles}
        />
      )}

      {showModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-primary text-light">
                <h5>{isEditing ? 'Edit Product' : 'Add Product'}</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  aria-label="Close"
                  onClick={() => setShowModal(false)}
                />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  {/* Product Code */}
                  <div className="col-md-4">
                    <label className="form-label">Product Code</label>
                    <input
                      type="text"
                      name="product_code"
                      className="form-control"
                      value={form.product_code}
                      onChange={handleInput}
                      disabled={formLoading}
                    />
                    {backendMsg.product_code && (
                      <div className="text-danger small">{backendMsg.product_code[0]}</div>
                    )}
                  </div>

                  {/* Name */}
                  <div className="col-md-4">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      name="name"
                      className="form-control"
                      value={form.name}
                      onChange={handleInput}
                      disabled={formLoading}
                    />
                    {backendMsg.name && (
                      <div className="text-danger small">{backendMsg.name[0]}</div>
                    )}
                  </div>

                  {/* Category */}
                  <div className="col-md-4">
                    <label className="form-label">Category</label>
                    <input
                      type="text"
                      name="category"
                      className="form-control"
                      value={form.category}
                      onChange={handleInput}
                      disabled={formLoading}
                    />
                  </div>

                  {/* Unit */}
                  <div className="col-md-4">
                    <label className="form-label">Unit</label>
                    <input
                      type="text"
                      name="unit"
                      className="form-control"
                      value={form.unit}
                      onChange={handleInput}
                      disabled={formLoading}
                    />
                  </div>

                  {/* Description */}
                  <div className="col-md-4">
                    <label className="form-label">Description</label>
                    <input
                      type="text"
                      name="description"
                      className="form-control"
                      value={form.description}
                      onChange={handleInput}
                      disabled={formLoading}
                    />
                  </div>

                  {/* Cost Price */}
                  <div className="col-md-4">
                    <label className="form-label">Cost Price</label>
                    <input
                      type="number"
                      name="cost_price"
                      className="form-control"
                      value={form.cost_price}
                      onChange={handleInput}
                      disabled={formLoading}
                    />
                  </div>

                  {/* Sale Price */}
                  <div className="col-md-4">
                    <label className="form-label">Sale Price</label>
                    <input
                      type="number"
                      name="sale_price"
                      className="form-control"
                      value={form.sale_price}
                      onChange={handleInput}
                      disabled={formLoading}
                    />
                  </div>

                  {/* Stock */}
                  <div className="col-md-4">
                    <label className="form-label">Stock</label>
                    <input
                      type="number"
                      name="stock"
                      className="form-control"
                      value={form.stock}
                      onChange={handleInput}
                      disabled={formLoading}
                    />
                  </div>

                  {/* Location */}
                  <div className="col-md-4">
                    <label className="form-label">Location</label>
                    <input
                      type="text"
                      name="location"
                      className="form-control"
                      value={form.location}
                      onChange={handleInput}
                      disabled={formLoading}
                    />
                  </div>

                  {/* Active Checkbox */}
                  <div className="col-md-4 d-flex align-items-center">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={form.is_active}
                      onChange={handleInput}
                      disabled={formLoading}
                      className="form-check-input me-2"
                    />
                    <label className="form-check-label">Active</label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={formLoading}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSubmit} disabled={formLoading}>
                  {formLoading ? 'Saving...' : isEditing ? 'Update' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
